import { addBreadcrumb, captureException } from '@sentry/browser';

import { AnalyticsPlatform } from '../types';

interface QueuedEvent {
  eventName: string;
  eventPayload: unknown;
  timestamp: number;
  onEventSent?: () => void;
}

export class GTMPlatform implements AnalyticsPlatform {
  private static countriesCodeBlacklist = ['IR'];
  private static instance: GTMPlatform | null = null;

  private currentCountry: string = '';
  private currentLanguage: string = '';
  private eventQueue: QueuedEvent[] = [];
  private readonly MAX_QUEUE_SIZE = 100; // Максимум 100 событий в очереди
  private readonly QUEUE_CLEANUP_INTERVAL = 300000; // 5 минут - очистка старых событий
  private lastQueueCleanup = 0;

  constructor() {
    if (GTMPlatform.instance) {
      return GTMPlatform.instance;
    }
    GTMPlatform.instance = this;

    // Добавляем обработчик для отправки событий при покидании страницы
    this.setupBeforeUnloadHandler();
  }

  private setupBeforeUnloadHandler() {
    if (typeof window !== 'undefined') {
      // Обработчик для beforeunload (срабатывает при попытке покинуть страницу)
      window.addEventListener('beforeunload', () => {
        this.sendQueuedEventsOnUnload();
      });

      // Обработчик для pagehide (более надежно срабатывает при закрытии вкладки)
      window.addEventListener('pagehide', () => {
        this.sendQueuedEventsOnUnload();
      });
    }
  }

  private sendQueuedEventsOnUnload() {
    if (this.eventQueue.length > 0) {
      // Принудительно отправляем все события из очереди
      this.eventQueue.forEach((event) => {
        try {
          window.dataLayer.push({
            event: event.eventName,
            ...(event.eventPayload as Record<string, unknown>),
          });

          // Вызываем callback после отправки
          if (event.onEventSent) {
            event.onEventSent();
          }

          addBreadcrumb({
            message: `Unload event sent: ${event.eventName}`,
            category: 'gtm.unload_success',
            level: 'info',
            data: { eventName: event.eventName },
          });
        } catch (error) {
          captureException(new Error(`Failed to send unload event: ${event.eventName}`), {
            extra: { error },
          });
        }
      });

      // Очищаем очередь
      this.eventQueue = [];
    }
  }

  private checkAndSendQueuedEvents() {
    if (this.isGtmLoaded() && this.eventQueue.length > 0) {
      this.sendQueuedEvents();
    }
  }

  isGtmLoaded() {
    window.dataLayer = window.dataLayer || [];
    const gtmStartedEvent = window.dataLayer.find((element) => element['gtm.start']);
    if (!gtmStartedEvent) {
      return false; // Not even the GTM inline config has executed
    } else if (!gtmStartedEvent['gtm.uniqueEventId']) {
      addBreadcrumb({
        message: 'Main GTM js is not loaded (likely AdBlock, NoScript, URL blocking etc.',
      });
      return false;
    }
    return true; // GTM is fully loaded and working
  }

  private cleanupOldEvents() {
    const now = Date.now();
    if (now - this.lastQueueCleanup > this.QUEUE_CLEANUP_INTERVAL) {
      this.eventQueue = this.eventQueue.filter(
        (event) => now - event.timestamp < this.QUEUE_CLEANUP_INTERVAL,
      );
      this.lastQueueCleanup = now;
    }
  }

  private addToQueue(eventName: string, eventPayload: unknown, onEventSent?: () => void) {
    this.cleanupOldEvents();

    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      // Удаляем самое старое событие
      this.eventQueue.shift();
    }

    this.eventQueue.push({
      eventName,
      eventPayload,
      timestamp: Date.now(),
      onEventSent,
    });

    addBreadcrumb({
      message: `Event queued: ${eventName}`,
      category: 'gtm.queue.add',
      level: 'info',
      data: { eventName, queueLength: this.eventQueue.length },
    });

    // Проверяем готовность GTM после добавления в очередь
    this.checkAndSendQueuedEvents();
  }

  private sendQueuedEvents() {
    if (this.eventQueue.length === 0) return;

    const eventsToSend = [...this.eventQueue];
    this.eventQueue = [];

    eventsToSend.forEach((event) => {
      try {
        window.dataLayer.push({
          event: event.eventName,
          ...(event.eventPayload as Record<string, unknown>),
        });
        addBreadcrumb({
          message: `Queued GTM event sent: ${event.eventName}`,
          category: 'gtm.queued_success',
          level: 'info',
          data: { eventName: event.eventName, eventPayload: event.eventPayload },
        });

        // Вызываем callback после успешной отправки из очереди
        if (event.onEventSent) {
          event.onEventSent();
        }
      } catch {
        captureException(new Error(`Failed to send queued GTM event: ${event.eventName}`));
      }
    });
  }

  waitForGTM = <T>(
    callback: () => void,
    maxWait = 10000,
    eventName: string,
    eventPayload: T,
    onEventSent?: () => void,
  ) => {
    let done = false;
    let waited = 0;
    const interval = 50;

    const check = () => {
      if (done) return;
      const isGTMReady = this.isGtmLoaded();

      // console.debug('GTM waitForGTM check:', {
      //   isGTMReady,
      //   waited,
      //   maxWait,
      //   eventName,
      //   dataLayerLength: window.dataLayer?.length,
      // });

      // Если GTM готов - сразу отправляем, не ждем теги внутри GTM
      if (isGTMReady) {
        clearInterval(timer);
        done = true;

        console.debug('GTM is ready, sending event immediately');

        // GTM готов - отправляем событие сразу
        window.dataLayer.push({ event: eventName, ...eventPayload });

        addBreadcrumb({
          message: `GTM event sent: ${eventName}`,
          category: 'gtm.success',
          level: 'info',
          data: { eventName, eventPayload },
        });

        // Вызываем callback после успешной отправки
        if (onEventSent) {
          onEventSent();
        }

        callback();
        return;
      }

      // Если GTM не готов и превышен таймаут
      if (waited >= maxWait) {
        clearInterval(timer);
        done = true;

        console.debug('GTM timeout, queuing event');

        // Если GTM не загрузился, добавляем событие в очередь
        this.addToQueue(eventName, eventPayload, onEventSent);

        addBreadcrumb({
          message: `GTM not ready, event queued: ${eventName}`,
          category: 'gtm.queue',
          data: eventPayload as object,
          level: 'warning',
        });

        callback();
      }

      waited += interval;
    };

    const timer = setInterval(check, interval);
  };

  public setCountry(country: string): void {
    this.currentCountry = country;
  }

  public setLanguage(language: string): void {
    this.currentLanguage = language;
    // Отправляем язык в dataLayer для использования в GTM
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        user_language: language,
      });
    }
  }

  public async sendEvent<T>(eventName: string, eventPayload: T, onEventSent?: () => void) {
    if (GTMPlatform.countriesCodeBlacklist.includes(this.currentCountry)) return;

    this.waitForGTM(
      () => {
        // Событие уже отправлено в waitForGTM, если GTM был готов
        // Здесь только логируем результат
        addBreadcrumb({
          message: `GTM sendEvent completed: ${eventName}`,
          category: 'gtm.send_complete',
          level: 'info',
          data: { eventName, eventPayload },
        });
      },
      10000,
      eventName,
      eventPayload,
      onEventSent,
    );
  }
  // TODO: когда подключим Partytown, тогда будет работать
  // if (typeof window !== 'undefined' && window.dataLayer) {
  //   // Прямой вызов dataLayer.push - будет перенаправлен через Partytown
  //   window.dataLayer.push({
  //     event: eventName,
  //     ...eventPayload,
  //   });
  //   console.debug(`GTM event via Partytown: ${eventName}`, eventPayload);
  // } else {
  //   // Fallback на TagManager если dataLayer не найден
  //   TagManager.dataLayer({ dataLayer: { event: eventName, ...eventPayload } });
  // }
}
